import type { QuestionnaireResponse, QuestionnaireResultResponse } from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { action, makeAutoObservable, observe } from 'mobx';

import { FiBankStore } from 'stores/FiBankStore';
import { SnackbarStore } from 'stores/SnackBarStore';
import { getEffectiveCompletedOn } from 'ui/apps/fi/util/questionnaire';
import { DateTimeHelpers } from 'util/dateTime.util';
import { model as parentModel } from '../../model';
import type { PM } from './questionnaire.repo';
import { QuestionnaireRepo } from './questionnaire.repo';

export interface VM {
  bankId: () => string;
  commentCount: number;
  isLoading: boolean;
  loadingFormValues: boolean;
  questionnaireResponse: QuestionnaireResponse;
  questionnaireResultResponse: QuestionnaireResultResponse;
  userFullname: string;
}

@injectable()
export class QuestionnairePresenter {
  constructor() {
    makeAutoObservable(this);
  }

  @inject(QuestionnaireRepo)
  private repo: QuestionnaireRepo;

  @inject(FiBankStore)
  bankStore: FiBankStore;

  @inject(SnackbarStore)
  private snackbarStore: SnackbarStore;

  viewModel: VM = {
    bankId: () => this.bankId,
    commentCount: 0,
    isLoading: true,
    loadingFormValues: true,
    questionnaireResponse: {} as QuestionnaireResponse,
    questionnaireResultResponse: {} as QuestionnaireResultResponse,
    userFullname: '--'
  };

  get bankId() {
    return this.bankStore.bank.id;
  }

  get timezone() {
    return this.bankStore.bank.iana_timezone;
  }

  private updateViewModel = action((viewModel: Partial<VM>) => {
    this.viewModel = { ...this.viewModel, ...viewModel };
  });

  load = action(async (questionnaireId: string, crbId: string, responseId: string) => {
    observe(this.repo, 'programmersModel', (obj) => {
      const programmersModel = obj.newValue as PM;

      this.updateViewModel({
        isLoading: programmersModel.isLoading,
        questionnaireResponse: programmersModel.questionnaireResponse,
        questionnaireResultResponse: programmersModel.questionnaireResultResponse,
        userFullname: this.getUser(programmersModel.questionnaireResultResponse)
      });
    });

    try {
      this.updateViewModel({ isLoading: true });
      await this.repo.load(questionnaireId, crbId, responseId);
      this.updateViewModel({ loadingFormValues: false });
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(`There was an issue fetching the questionnaire data.`);
    }
  });

  composeCompletionDate = (questionnaire: QuestionnaireResultResponse) => {
    const completedOn = getEffectiveCompletedOn(
      questionnaire.revision_history,
      questionnaire.custom_requirement_completed_on
    );
    return DateTimeHelpers.formatISOToTableDateString(completedOn, this.bankStore.bank.iana_timezone);
  };

  setCount = action((count: number) => {
    this.updateViewModel({ commentCount: count });
  });

  getUser = (questionnaireResultResponse: QuestionnaireResultResponse) => {
    if (parentModel.dispensaryStaff.length && questionnaireResultResponse.custom_requirement_completed_by) {
      const user = parentModel.dispensaryStaff.find(
        (u) => u.id === questionnaireResultResponse.custom_requirement_completed_by
      );
      if (questionnaireResultResponse.custom_requirement_completed_by === 'AUTO_GENERATED') {
        return 'Autocompleted';
      } else {
        return user ? `${user?.firstName} ${user?.lastName}` : '--';
      }
    } else if (
      parentModel.dispensaryStaff.length &&
      questionnaireResultResponse.custom_requirement_modified_by
    ) {
      const user = parentModel.dispensaryStaff.find(
        (u) => u.id === questionnaireResultResponse.custom_requirement_modified_by
      );
      return user ? `${user?.firstName} ${user?.lastName}` : '--';
    } else {
      return '--';
    }
  };
}
